import classNames from "classnames";
import React, { useState } from "react";
import { toast } from "react-toastify";

import {
  SupportTicketDetail,
  useCancelSupportScheduleMutation,
  useRescheduleSupportEmailMutation,
  useSendSupportScheduledNowMutation,
} from "../../slices/SupportSlice";
import {
  browserTimeZone,
  formatSendTime,
  recipientsLine,
} from "./schedule.utils";
import { ClockIcon, ScheduleSendButton } from "./ScheduleSendButton";
import { errorText } from "./support.utils";
import { SMALL_BUTTON } from "./supportUi";

/**
 * The case's scheduled email (owner 2026-11-03): when it goes out, its text, and Send now / Edit time / Cancel.
 * Cancel puts the text back into the reply box (to change the text: cancel, edit, schedule again). An email that
 * couldn't be sent after 3 tries shows "Couldn't send" with the reason.
 */
export const ScheduledEmailCard = ({
  ticket,
}: {
  ticket: SupportTicketDetail;
}) => {
  const s = ticket.scheduled;
  const [confirming, setConfirming] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sendNow, { isLoading: isSending }] =
    useSendSupportScheduledNowMutation();
  const [reschedule, { isLoading: isRescheduling }] =
    useRescheduleSupportEmailMutation();
  const [cancel, { isLoading: isCancelling }] =
    useCancelSupportScheduleMutation();
  if (!s) return null;
  const failed = s.status === "failed";
  const sending = s.status === "sending";
  const busy = isSending || isRescheduling || isCancelling || sending;
  const long = s.text.length > 280 || s.text.split("\n").length > 5;

  const doSendNow = async () => {
    try {
      await sendNow(ticket.id).unwrap();
      toast(`Sent to ${ticket.contactEmail}.`, { type: "success" });
    } catch (e) {
      toast(errorText(e, "The email wasn't sent. Please try again."), {
        type: "error",
      });
    }
  };
  const doReschedule = async (at: Date) => {
    try {
      const d = await reschedule({
        id: ticket.id,
        sendAt: at.toISOString(),
        timeZone: browserTimeZone(),
      }).unwrap();
      toast(`Now goes out ${formatSendTime(d.scheduled?.sendAt || at)}.`, {
        type: "success",
      });
    } catch (e) {
      toast(errorText(e, "The time wasn't changed. Please try again."), {
        type: "error",
      });
    }
  };
  const doCancel = async () => {
    try {
      await cancel(ticket.id).unwrap();
      setConfirming(false);
      toast(
        s.cc?.length || s.bcc?.length
          ? "Cancelled. The email and its Cc / Bcc are back in the reply box."
          : "Cancelled. The email is back in the reply box.",
        { type: "info" },
      );
    } catch (e) {
      toast(errorText(e, "It wasn't cancelled. Please try again."), {
        type: "error",
      });
    }
  };

  return (
    <div
      data-testid={"scheduled-card"}
      className={classNames(
        "mb-3 rounded-xl border-[1.5px] px-4 py-3",
        failed
          ? "border-[#F3C2BC] bg-[#FFF6F5]"
          : "border-[#DCD3FF] bg-[#FBFAFF]",
      )}>
      <div className={"flex flex-wrap items-center gap-2"}>
        <span
          className={classNames(
            "inline-flex items-center gap-1.5 text-[14px] font-extrabold",
            failed ? "text-[#B42318]" : "text-[#4B32D6]",
          )}>
          <ClockIcon />
          {failed
            ? "Couldn't send"
            : sending
            ? "Sending now…"
            : `Scheduled for ${formatSendTime(s.sendAt)}`}
        </span>
        <span className={"text-[12.5px] text-[#5D6475]"}>
          {failed
            ? `Was due ${formatSendTime(s.sendAt)}${
                s.attempts > 1 ? ` · tried ${s.attempts} times` : ""
              }`
            : `${recipientsLine(ticket.contactEmail, s.cc, s.bcc)}${
                s.then === "solved"
                  ? " · then Solved"
                  : " · then waits for their answer"
              }`}
        </span>
      </div>
      {failed && s.error && (
        <div className={"mt-1.5 text-[13px] text-[#7A271A]"}>{s.error}</div>
      )}
      <div
        className={classNames(
          "mt-2 whitespace-pre-wrap rounded-lg border border-[#EDEFF3] bg-white px-3 py-2 text-[13.5px] leading-relaxed text-[#3A4256]",
          !expanded && long && "max-h-[96px] overflow-hidden",
        )}>
        {s.text}
      </div>
      {long && (
        <button
          type={"button"}
          onClick={() => setExpanded((v) => !v)}
          className={
            "mt-1 text-[12.5px] font-semibold text-[#6B4EFF] hover:underline"
          }>
          {expanded ? "Show less" : "Show all"}
        </button>
      )}
      <div className={"mt-2.5 flex flex-wrap items-center gap-2"}>
        <button
          type={"button"}
          disabled={busy}
          onClick={() => void doSendNow()}
          className={classNames(SMALL_BUTTON, "h-10")}
          data-testid={"scheduled-send-now"}>
          {isSending ? "Sending…" : "Send now"}
        </button>
        <ScheduleSendButton
          label={failed ? "Try again at…" : "Edit time"}
          busy={isRescheduling}
          disabled={busy}
          initial={
            new Date(s.sendAt) > new Date() ? new Date(s.sendAt) : undefined
          }
          onSchedule={(at) => void doReschedule(at)}
          testId={"scheduled-edit-time"}
        />
        {confirming ? (
          <span
            className={
              "inline-flex flex-wrap items-center gap-2 text-[13px] text-[#3A4256]"
            }>
            Cancel it? The text goes back into the reply box.
            <button
              type={"button"}
              disabled={busy}
              onClick={() => void doCancel()}
              className={classNames(SMALL_BUTTON, "h-9 text-[#C0392B]")}
              data-testid={"scheduled-cancel-yes"}>
              {isCancelling ? "Cancelling…" : "Yes, cancel"}
            </button>
            <button
              type={"button"}
              onClick={() => setConfirming(false)}
              className={classNames(SMALL_BUTTON, "h-9")}>
              Keep it
            </button>
          </span>
        ) : (
          <button
            type={"button"}
            disabled={busy}
            onClick={() => setConfirming(true)}
            title={"Cancel, to change the text or send it another way"}
            className={classNames(SMALL_BUTTON, "h-10 text-[#C0392B]")}
            data-testid={"scheduled-cancel"}>
            Cancel
          </button>
        )}
      </div>
      <div className={"mt-2 text-[12px] text-[#7A7F8C]"}>
        To change the text, cancel it (the text comes back into the reply box),
        edit it and schedule it again.
      </div>
    </div>
  );
};
